import { useCallback, useEffect, useState } from 'react';
import { notification } from 'antd';
import moment from 'moment-timezone';
import { useNavigate } from 'react-router-dom';
import APIClient from '../api/api';
import { RenewalJob } from '../api/models';
import { Button, Column, DataTable, Pagination, StatusTag } from '../components/ui';
import { PageFooter, PageHead } from '../layout';
import BulkRenewalModal from '../components/BulkRenewalModal';
import { formatCents, isTerminalJobStatus, jobStatusTone } from '../components/BulkRenewal.model';

const DEFAULT_PAGE_SIZE = 25;
const RUNNING_REFRESH_MS = 10000;

interface Props {
  /** Override for tests. */
  refreshIntervalMs?: number;
}

const Renewals = ({ refreshIntervalMs = RUNNING_REFRESH_MS }: Props) => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<RenewalJob[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  const load = useCallback(async (targetPage: number) => {
    try {
      const resp = await APIClient.listRenewalJobs(targetPage, DEFAULT_PAGE_SIZE);
      setJobs(resp.content);
      setTotal(resp.total);
    } catch (e: any) {
      notification.error({ message: e?.response?.data?.error || 'Could not load renewals' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(page);
  }, [load, page]);

  // A run in flight changes underneath the list, so keep refreshing while one
  // is still working and stop once they have all settled.
  const hasRunning = jobs.some((job) => !isTerminalJobStatus(job.status));
  useEffect(() => {
    if (!hasRunning) return;
    let cancelled = false;
    const timer = window.setTimeout(() => {
      if (!cancelled) load(page);
    }, refreshIntervalMs);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [hasRunning, jobs, load, page, refreshIntervalMs]);

  const columns: Column<RenewalJob>[] = [
    {
      key: 'created',
      title: 'Started',
      render: (job) => moment(job.createdAt).format('YYYY-MM-DD HH:mm'),
    },
    { key: 'account', title: 'Account', render: (job) => job.ncUsername },
    { key: 'requestedBy', title: 'Requested by', render: (job) => job.requestedBy },
    { key: 'years', title: 'Term', align: 'right', render: (job) => `${job.years}y` },
    {
      key: 'progress',
      title: 'Domains',
      align: 'right',
      render: (job) => `${job.succeeded + job.failed} / ${job.totalDomains}`,
    },
    {
      key: 'failed',
      title: 'Failed',
      align: 'right',
      render: (job) => (job.failed > 0 ? <span className="text-danger">{job.failed}</span> : '0'),
    },
    {
      key: 'charged',
      title: 'Charged',
      align: 'right',
      render: (job) => formatCents(job.chargedTotalCents, job.currency),
    },
    {
      key: 'estimated',
      title: 'Estimated',
      align: 'right',
      render: (job) => formatCents(job.estimatedTotalCents, job.currency),
    },
    {
      key: 'status',
      title: 'Status',
      render: (job) => <StatusTag tone={jobStatusTone(job.status)}>{job.status}</StatusTag>,
    },
  ];

  return (
    <>
      <PageHead
        title="Renewals"
        subtitle="Bulk domain renewals run through the Namecheap API."
        actions={
          <Button variant="ghost" onClick={() => setShowModal(true)}>
            New Bulk Renewal
          </Button>
        }
      />

      <DataTable
        columns={columns}
        data={jobs}
        rowKey={(job) => String(job.id)}
        loading={loading}
        emptyMessage="No renewals have been run yet."
        rowHref={(job) => `/renewals/${job.id}`}
        onRowClick={(job) => navigate(`/renewals/${job.id}`)}
      />

      <PageFooter
        right={
          <Pagination
            page={page}
            pageSize={DEFAULT_PAGE_SIZE}
            total={total}
            onPageChange={(next: number) => setPage(next)}
          />
        }
      />

      {showModal && (
        <BulkRenewalModal
          onClose={() => setShowModal(false)}
          onStarted={(jobId) => {
            setShowModal(false);
            navigate(`/renewals/${jobId}`);
          }}
        />
      )}
    </>
  );
};

export default Renewals;
